<template>
    <div>
        <!-- Selected items UI -->
        <q-input
            ref="input"
            @click="showDialog = true"
            @clear="clearSelection"
            v-model="selectedItemName"
            :input-class="{
                disable: 'table-select-input__disabled'
            }"
            :input-style="{
                cursor: 'pointer',
                fontSize: getFontSize
            }"
            :disable="disable"
            :label="label"
            :dense="dense"
            :rules="validationRules"
            :clearable="clearable"
            lazy-rules
            outlined
            :readonly="true"
            color="accent"
        >
            <template v-slot:after>
                <slot name="input"></slot>
            </template>
        </q-input>

        <!-- Dialog -->
        <q-dialog v-model="showDialog">
            <q-card :class="$q.screen.gt.lg ? 'dialog__lg' : 'dialog__xs'">
                <!-- Dialog header -->
                <q-bar class="bg-primary">
                    <div class="text-white">
                        {{ dialogTitle }}
                    </div>

                    <q-space />

                    <q-btn
                        @click="close"
                        class="text-white"
                        icon="close"
                        dense
                        flat
                    >
                        <q-tooltip>{{ $t('Close') }}</q-tooltip>
                    </q-btn>
                </q-bar>

                <!-- Reload options -->
                <q-card-section v-if="loadingOptionsFailed">
                    <q-banner inline-actions class="text-black outline">
                        {{
                            $t(
                                'Failed to load items due to an unexpected error. Click on the button to reload options.'
                            )
                        }}
                        <template v-slot:action>
                            <q-btn
                                @click="getItems"
                                :loading="isLoading"
                                :label="$t('Reload')"
                                color="primary"
                            />
                        </template>
                    </q-banner>
                </q-card-section>

                <!-- Table -->
                <q-card-section v-else>
                    <div
                        class="row fit q-mb-sm"
                        :class="
                            $q.screen.lt.sm
                                ? ''
                                : 'wrap justify-between items-start content-start'
                        "
                    >
                        <q-input
                            v-model="search"
                            @clear="search = ''"
                            :placeholder="searchLabel"
                            :class="
                                $q.screen.lt.sm
                                    ? 'col-12 q-mb-sm'
                                    : 'col q-mr-sm'
                            "
                            debounce="500"
                            clearable
                            outlined
                            dense
                            color="accent"
                        />
                        <q-select
                            v-model="selectedCategory"
                            :options="loadedCategories"
                            :loading="loading"
                            label="Select Category"
                            use-input
                            @input-value="debounceSearch"
                            @virtual-scroll="onScrollCategory"
                            clearable
                            dense
                            outlined
                            behavior="menu"
                            option-value="name"
                            option-label="name"
                            :class="
                                $q.screen.lt.sm
                                    ? 'col-12 q-mb-sm'
                                    : 'col-4 q-mr-sm '
                            "
                            @popup-hide="handlePopupHide"
                            color="accent"
                        >
                        </q-select>
                    </div>
                    <q-table
                        @request="onRequest"
                        :rows="data"
                        :columns="columns"
                        :filter="searchValue"
                        v-model:pagination="pagination"
                        :rows-per-page-options="[30]"
                        v-model:selected="selected"
                        :loading="isLoading"
                        :row-key="rowKey"
                        :no-data-label="noDataLabel || $t('No data available')"
                        :grid="cardMode"
                        table-class="sticky-table table-mode"
                        card-container-class="card-mode"
                        selection="single"
                        binary-state-sort
                        bordered
                        flat
                        hide-selected-banner
                    >
                        <!-- Card Mode -->
                        <template v-slot:item="props">
                            <slot name="item" :props="props"></slot>
                        </template>
                    </q-table>
                </q-card-section>

                <!-- Dialog actions -->
                <q-card-section
                    v-if="!loadingOptionsFailed"
                    class="float-right"
                >
                    <q-btn
                        @click="close"
                        class="q-mr-md"
                        color="white"
                        text-color="primary"
                        outline
                        >{{ $t('Cancel') }}</q-btn
                    >
                    <q-btn @click="confirm" color="primary">{{
                        $t('Confirm')
                    }}</q-btn>
                </q-card-section>
            </q-card>
        </q-dialog>
    </div>
</template>

<script>
import TableSelectMixin from '@mixins/TableSelectMixin.vue';

const FONT_SIZE_MAPPING = {
    small: '0.5rem',
    normal: '0.9rem',
    large: '1.2rem'
};
// Fields defined fot Category filter virtual scroll

export default {
    name: 'CauseTableSelect',
    mixins: [TableSelectMixin],
    props: {
        /*********************
         * Category management.*
         ********************/
        facilityId: {
            type: [String, Number],
            required: false
        },
        /**
         * The size of the button
         * @values small, normal, large
         */
        fontSize: {
            type: String,
            default: 'normal'
        },
        noDataLabel: {
            type: String,
            required: false,
            default: null
        }
    },
    watch: {
        /**
         * Reinitialize the component is the computed url changes
         */
        async categoriesUrl(value) {
            await this.initCategorySelection();
        }
    },
    data() {
        return {
            FONT_SIZE_MAPPING,
            // Fields to load Categories
            loadedCategories: [],
            selectedCategory: null,
            nextPage: null,
            loading: false,
            debounce: null,
            categoryPagination: {
                sortBy: '',
                descending: false,
                page: 1,
                rowsPerPage: 30,
                rowsNumber: null
            },
            categoriesSearchVal: ''
        };
    },
    async mounted() {
        if (this.loadedCategories.length < 1) {
            // initialize the list of items
            await this.initCategorySelection();
        }
    },
    computed: {
        /**
         * Return a filter string by combining the available filter fields
         * @return {String}
         */
        searchValue() {
            if (this.selectedCategory !== null) {
                return this.search + ' ' + this.selectedCategory.name;
            }
            return this.search;
        },
        /**
         * Url to fetch data from. It combines the baseUrl with url parameters
         * @return {String}
         */
        categoriesUrl() {
            // Update URL
            const { descending, page, sortBy } = this.categoryPagination;
            return this.updateURL({
                baseURL: `facilities/${this.facilityId}/categories/`,
                params: {
                    search: this.categoriesSearchVal,
                    ordering: descending ? `-${sortBy}` : `${sortBy}`,
                    page: page,
                    // include initial url params if provided
                    ...this.queryParams
                }
            });
        },
        /**
         * Return the font size to use for the input
         * @return {String}
         */
        getFontSize() {
            return FONT_SIZE_MAPPING[this.fontSize];
        }
    },
    methods: {
        /**
         * Load Categories from server
         * @return {Object} - response
         * @return {Object} - error
         */
        async loadCategories(url) {
            return new Promise((resolve, reject) => {
                this.$axios
                    .get(url)
                    .then(response => {
                        resolve(response);
                    })
                    .catch(error => {
                        reject(error);
                    });
            });
        },
        /**
         * Init the Table select and get the first page of the categories
         */
        async initCategorySelection() {
            if (this.categoriesUrl) {
                // start loading
                this.loading = true;
                this.nextPage = null;
                const errorMessage = this.$t(
                    'Failed to load categories. Please try again later.'
                );
                await this.loadCategories(this.categoriesUrl)
                    .then(response => {
                        // reset the loaded item list
                        this.loadedCategories = [];
                        const results = response.data.results;
                        this.loadedCategories.push(...results);
                        // get the url of the next page
                        this.nextPage = response.data.next;
                    })
                    .catch(error => {
                        this.$notifyError(errorMessage, error);
                    })
                    .finally(() => {
                        // stop loading
                        this.loading = false;
                    });
            }
        },
        /**
         * Virtual scroll behavior
         * When the cursor scrolls to the bottom of the dropdown
         * fetch more options from server and push them to loaded item list
         * then update the next page number
         *
         * @param {Number} index - The index of the last item that was scrolled into view.
         * @param {Object} ref - Vue reference to the QVirtualList that triggered the event.
         */
        async onScrollCategory({ index, ref }) {
            // calculate the last index of the current option list
            const loadingIndex = this.loadedCategories.length - 15;
            // check if there are more available options
            if (
                this.loading !== true &&
                this.nextPage !== null &&
                index >= loadingIndex
            ) {
                this.loading = true;
                const errorMessage = this.$t(
                    'Failed to load categories. Please try again later.'
                );
                await this.loadCategories(this.nextPage)
                    .then(response => {
                        const results = response.data.results;
                        this.loadedCategories.push(...results);
                        this.nextPage = response.data.next;
                        this.$nextTick(() => {
                            ref.refresh();
                        });
                    })
                    .catch(error => {
                        this.$notifyError(errorMessage, error);
                    })
                    .finally(() => {
                        // stop loading
                        this.loading = false;
                    });
            }
        },
        /**
         * Reset search results when dropdown/option selector is closed.
         */
        async handlePopupHide() {
            // reset the search query
            this.categoriesSearchVal = '';
        },
        /**
         * Set up search function with a debounce time
         */
        async debounceSearch(value) {
            clearTimeout(this.debounce);
            this.debounce = setTimeout(async () => {
                // set the search content to user input
                this.categoriesSearchVal = value;
            }, 300);
        }
    }
};
</script>

<style lang="scss" scoped>
.dialog__lg {
    width: 50%;
    max-width: none;
}

.dialog__xs {
    width: 100%;
    max-width: none;
}

// scroll area for the cards container of the component
:deep(.card-mode) {
    max-height: 50vh;
    overflow-y: scroll;
}

// scroll area for the table rows part of the component
:deep(.table-mode) {
    height: 55vh;
}
</style>
